import React, { useState, useEffect } from 'react';
import LeaseApiService from '../../services/apiService';
import { FaSave, FaChevronLeft } from 'react-icons/fa';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getName } from '../../../Redux/selecters';
import CommunityDetails from './communityDetails';
import {
  fetchCommunityByRecordId,
  fetchCommunityCountries,
  fetchCommunityCitiesByCountry,
  pickCommunityField,
  resolveCommunityRecordId,
  toSelectOption,
} from './communityApiHelpers';
import '../customerMaster/customerForm.css';

const UpdateCommunity = ({ initialData, viewOnly: viewOnlyProp, onClose, onSaved } = {}) => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const navigate = useNavigate();
  const location = useLocation();
  const { recordId: routeRecordId } = useParams();
  const [searchParams] = useSearchParams();
  const userData = initialData ?? location.state;
  const viewOnly = Boolean(viewOnlyProp ?? (searchParams.get('mode') === 'view' || userData?.viewOnly));
  const userName = useSelector(getName);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [formData, setFormData] = useState({
    recordId: '',
    communityNo: '',
    descriptionEn: '',
    descriptionAr: '',
    country: null,
    city: null,
    marketingDescription: '',
    remarks: '',
    createdBy: '',
    createdOn: '',
  });
  const [countriesList, setCountriesList] = useState([]);
  const [cityList, setCityList] = useState([]);

  useEffect(() => {
    const recordId = routeRecordId || resolveCommunityRecordId(userData);
    if (!recordId) {
      toast.warning(t('communityMaster.notFound'));
      if (onClose) {
        onClose();
      } else {
        navigate('/Customer/Property/Master/Community/Listing');
      }
      return;
    }
    loadCommunityData(recordId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeRecordId]);

  const handleBackClick = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate('/Customer/Property/Master/Community/Listing');
  };

  const loadCities = async (countryLabel) => {
    if (!countryLabel) {
      setCityList([]);
      return [];
    }
    const items = await fetchCommunityCitiesByCountry(apiService, countryLabel);
    setCityList(items);
    return items;
  };

  const populateForm = async (apiData, recordId) => {
    const countries = await fetchCommunityCountries(apiService);
    setCountriesList(countries);

    const countryLabel = pickCommunityField(apiData, 'country', 'Country');
    const cityLabel = pickCommunityField(apiData, 'city', 'City');
    const countryOption = toSelectOption(countries, countryLabel);

    let cityOption = null;
    if (countryOption?.label) {
      const cities = await loadCities(countryOption.label);
      cityOption = toSelectOption(cities, cityLabel);
    }

    setFormData({
      recordId: resolveCommunityRecordId(apiData) || recordId,
      communityNo: pickCommunityField(apiData, 'communityNo', 'CommunityNo'),
      descriptionEn: pickCommunityField(apiData, 'description', 'Description'),
      descriptionAr: pickCommunityField(apiData, 'descriptionBI', 'DescriptionBI'),
      country: countryOption,
      city: cityOption,
      marketingDescription: pickCommunityField(apiData, 'marketingDescription', 'MarketingDescription'),
      remarks: pickCommunityField(apiData, 'remarks', 'Remarks'),
      createdBy: apiData?.createdBy || apiData?.CreatedBy || '',
      createdOn: apiData?.createdOn || apiData?.CreatedOn || '',
    });
  };

  const loadCommunityData = async (recordId) => {
    try {
      setTableLoading(true);

      let apiData = null;
      try {
        apiData = await fetchCommunityByRecordId(apiService, recordId);
      } catch (fetchError) {
        const hasListingFallback = userData && (resolveCommunityRecordId(userData) || pickCommunityField(userData, 'communityNo', 'CommunityNo'));
        if (hasListingFallback) {
          apiData = userData;
        } else {
          throw fetchError;
        }
      }

      await populateForm(apiData, recordId);
    } catch (error) {
      console.error('Error loading community:', error);
      const message = String(error?.message || '');
      const isSuccessMessage = /fetched successfully|retrieved successfully|successfully/i.test(message);
      if (!isSuccessMessage) {
        toast.error(message || t('communityMaster.loadFailed'));
        if (onClose) {
          onClose();
        } else {
          navigate('/Customer/Property/Master/Community/Listing');
        }
      }
    } finally {
      setTableLoading(false);
    }
  };

  const handleCountryChange = async (selectedOption) => {
    setFormData((prev) => ({ ...prev, country: selectedOption, city: null }));
    await loadCities(selectedOption?.label);
  };

  const handleSave = async () => {
    if (viewOnly) return;
    if (!formData.communityNo?.trim()) {
      toast.warning(t('communityMaster.validation.communityNoRequired'));
      return;
    }
    if (!formData.descriptionEn?.trim()) {
      toast.warning(t('communityMaster.validation.descriptionRequired'));
      return;
    }
    if (!formData.country) {
      toast.warning(t('communityMaster.validation.countryRequired'));
      return;
    }
    if (!formData.city) {
      toast.warning(t('communityMaster.validation.cityRequired'));
      return;
    }

    try {
      setLoading(true);
      const currentDatetimeUTC = new Date().toISOString();
      const body = {
        community: {
          record_Id: Number(formData.recordId || routeRecordId || resolveCommunityRecordId(userData) || 0),
          communityNo: formData.communityNo || '0',
          description: formData.descriptionEn || '0',
          descriptionBI: formData.descriptionAr || '0',
          city: formData.city?.label || '0',
          country: formData.country?.label || '0',
          marketingDescription: formData.marketingDescription || '0',
          remarks: formData.remarks || '0',
          createdBy: formData.createdBy || userName || '',
          createdOn: formData.createdOn || currentDatetimeUTC,
          modifiedBy: userName || '',
          modifiedOn: currentDatetimeUTC,
        },
      };
      const res = await apiService.put('UPDATE_COMMUNITY', body);
      if (res?.data?.isSuccessful || [200, 201, 202].includes(Number(res?.data?.statusCode))) {
        toast.success(res?.data?.message || t('communityMaster.updateSuccess'));
        setLoading(false);
        if (onSaved) {
          onSaved();
        } else {
          navigate('/Customer/Property/Master/Community/Listing');
        }
      } else {
        toast.warning(res?.data?.message || t('communityMaster.updateFailed'));
        setLoading(false);
      }
    } catch (error) {
      console.error('Error updating community:', error);
      toast.error(t('communityMaster.updateFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column customer-modal-form cf-modal" style={{ height: '85vh' }}>
      <div className="cf-head d-flex align-items-center justify-content-between flex-shrink-0">
        <div>
          <h5 className="mb-0">{viewOnly ? t('communityMaster.viewTitle') : t('communityMaster.editTitle')}</h5>
          {viewOnly && <span className="text-muted small">{t('common.viewOnlyNotice')}</span>}
        </div>
        <button type="button" className="btn-close" aria-label="Close" onClick={handleBackClick}></button>
      </div>
      <div className="p-3" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <CommunityDetails
          formData={formData}
          setFormData={setFormData}
          countriesList={countriesList}
          cityList={cityList}
          onCountryChange={handleCountryChange}
          readOnly={viewOnly}
          tableLoading={tableLoading}
        />
      </div>
      <div className="cf-foot d-flex justify-content-end gap-3 flex-shrink-0">
        {!viewOnly &&
          (loading ? (
            <div className="mainbtn flex-end gap-2">
              <ClipLoader color="#ffffff" className="mt-1" size={20} />
            </div>
          ) : (
            <div className="mainbtn flex-end gap-2" onClick={handleSave}>
              <FaSave color="#fff" className="center mt-1 mx-2" />
              <div className="center">{t('common.save')}</div>
            </div>
          ))}
        <div className={viewOnly ? 'mainbtn flex-end gap-2' : 'mainbtnRed flex-end gap-2'} onClick={handleBackClick}>
          <FaChevronLeft color="#fff" className="center mt-1 mx-2" />
          <div className="center">{viewOnly ? t('common.close') : t('common.cancel')}</div>
        </div>
      </div>
    </div>
  );
};

export default UpdateCommunity;
